import React, { FC, MouseEvent, useCallback, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled, { createGlobalStyle, css } from 'styled-components';

import { Button, Currencies, Currency, Icon, Modal, ModalSize, formatNumber } from '@column/column-ui-kit';

import { Flag } from '~/components/Flag';
import { NotificationList } from '~/components/NotificationList';
import countryCodeJSON from '~/data/wires/country-codes.json';
import { useCreateCounterparty, useDeleteCounterparty, useInstitution } from '~/hooks';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useTransfersFetch } from '~/hooks/useTransfers';
import { Counterparty, CreateCounterparty, FinancialInstitution, ValidateIBANObject } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { Flex, Inner, Text } from '~/styles';
import { titleCase } from '~/util';

import { ContentAccount } from './_partial/Account';
import { ContentBank } from './_partial/Bank';
import { ContentOwner } from './_partial/Owner';

type StepID = 'bank' | 'account' | 'owner';
type StepState = 'active' | 'completed' | 'disabled';

export type StepType = {
  id: StepID;
  label: string;
  state: StepState;
};

export interface CountryCodeEntry {
  countryCode: string;
  currencyCode: string;
  countryName: string;
}

type TemporaryData = {
  institution: FinancialInstitution | null;
  institutionSidebar: boolean;
  iban: ValidateIBANObject | null;
  steps: StepType[];
  flagImageData: string | null;
  countryCodeEntry: CountryCodeEntry | null;
  currency: Currency | null;
  oldCounterpartyId: string | null;
};

export type FormData = CreateCounterparty & { temporaryData: TemporaryData };

export interface ModalInternationalCounterpartyProps {
  isOpen: boolean;
  onClose: () => void;
  values?: Counterparty;
  callback: (counterparty: Counterparty, currency?: Currency) => void;
}

const GlobalStyles = createGlobalStyle`
  @property --mask-size {
    syntax: '<percentage>';
    inherits: false;
    initial-value: 0%;
  }

  @property --mask-size-inner {
    syntax: '<percentage>';
    inherits: false;
    initial-value: 0%;
  }
`;

const ModalContainer = styled.div<{ $showSidebar: boolean }>`
  --modal-width: 500px;
  --modal-offset-x: 0px;

  position: relative;
  display: grid;
  transition: transform 0.3s;
  transform: translateX(calc(var(--modal-offset-x) * -1));

  ${({ $showSidebar }) =>
    $showSidebar &&
    css`
      --modal-offset-x: 132px;
    `}
`;

const Content = styled.div`
  grid-area: 1 / 1;
  position: relative;
  z-index: 2;
  border-radius: 12px;
  background-color: ${({ theme }) => theme.background};
  box-shadow:
    rgba(43, 49, 67, 0.075) 0px 0px 0px 1px,
    rgba(43, 49, 67, 0.075) 0px 1px 3px,
    rgba(43, 49, 67, 0.075) 0px 8px 20px,
    rgba(43, 49, 67, 0.024) 0px 4px 12px;
  width: var(--modal-width);
  display: flex;
  flex-direction: column;
`;

const ModalContentWrapper = styled.div`
  position: relative;
  flex: 1;
  overflow: auto;
  max-height: calc(100vh - 280px);

  &::before {
    content: '';
    position: sticky;
    top: 0;
    left: 0;
    right: 0;
    display: block;
    height: 24px;
    margin-bottom: -24px;
    background: linear-gradient(to bottom, ${({ theme }) => theme.background}, transparent);
    pointer-events: none;
    z-index: 4;
  }

  &::after {
    content: '';
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    display: block;
    height: 24px;
    margin-top: -24px;
    background: linear-gradient(to top, ${({ theme }) => theme.background}, transparent);
    pointer-events: none;
    z-index: 4;
  }
`;

const Tabs = styled.div`
  position: relative;
  overflow: clip;
  padding: 8px;
  margin: -8px;
`;

const Tab = styled.div<{ $isActive: boolean }>`
  transition: opacity 0.1s;

  ${({ $isActive }) =>
    !$isActive &&
    css`
      pointer-events: none;
      opacity: 0;
      visibility: hidden;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
    `}
`;

const Sidebar = styled.div`
  width: 276px;
  grid-area: 1 / 1;
  justify-self: end;

  z-index: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  box-sizing: border-box;
  padding: 20px 20px 20px 32px;
  border-radius: 0 12px 12px 0;
  background-color: ${({ theme }) => theme.secondary.blendToBackground(25)};
  transition: transform 0.3s;
  transform: translateX(calc(var(--modal-offset-x, 0px) * 2));
`;

const SidebarInner = styled.div<{ $showSidebar: boolean }>`
  --mask-size: 0%;
  --mask-size-inner: 0%;

  opacity: 0;
  transition:
    --mask-size 0.4s ease,
    --mask-size-inner 0.7s ease,
    opacity 0.4s;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  mask-image: radial-gradient(circle at 0% 50%, black var(--mask-size-inner), transparent var(--mask-size));

  ${({ $showSidebar }) =>
    $showSidebar &&
    css`
      --mask-size: 100%;
      --mask-size-inner: 100%;
      opacity: 1;
    `}
`;

const Blur = styled.div<{ $backgroundBlur?: string }>`
  position: absolute;
  inset: 1px;
  border-radius: 10px;
  display: none;
  overflow: hidden;
  filter: saturate(1.5) brightness(1.15);

  &:after {
    content: '';
    position: absolute;
    inset: -20px -5% 60% -5%;
    background-image: ${({ $backgroundBlur }) => ($backgroundBlur ? `url(${$backgroundBlur})` : 'none')};
    background-size: 170% 180%;
    filter: blur(35px);
    opacity: 0.4;
    background-position: center center;
  }
`;

const Steps = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 24px;
  padding: 0 24px;
  margin-top: -px;
  box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.1);
`;

const Step = styled.div<{ $state: StepState; $isFlag?: boolean }>`
  position: relative;
  display: flex;
  gap: 8px;
  padding: 12px 0;
  font-size: 14px;
  line-height: 18px;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  transition:
    background-color 0.2s,
    box-shadow 0.2s;

  ${({ $state }) =>
    $state === 'active' &&
    css`
      box-shadow: inset 0 -2px 0 ${({ theme }) => theme.primary.blendToBackground(900)};
      color: ${({ theme }) => theme.foreground};
    `}
`;

const StepLabel = styled.div`
  font-weight: 500;
`;

const StepNumber = styled.div<{ $state: StepState; $isFlag?: boolean }>`
  font-weight: 600;
  font-size: 11px;
  border-radius: 100%;
  width: 18px;
  height: 18px;
  text-align: center;
  background-color: ${({ theme }) => theme.secondary.blendToBackground(150)};
  color: ${({ theme }) => theme.secondary.blendToBackground(900)};
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(250)};

  ${({ $state }) =>
    $state === 'active' &&
    css`
      background-color: ${({ theme }) => theme.primary.blendToBackground(900)};
      color: ${({ theme }) => theme.secondary.blendToBackground(50)};
      box-shadow: inset 0 0 0 1px ${({ theme }) => theme.primary.blendToBackground(1000)};
    `}
`;

export const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-gap: 24px;
  align-items: start;
`;

export const Row = styled.div`
  grid-column: 1 / 3;
`;

const AddressIcon = styled.div`
  width: 68px;
  height: 68px;
  border-radius: 50%;
  margin: 4px auto 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.background};
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.1),
    0 4px 10px rgba(0, 0, 0, 0.05),
    0 4px 16px rgba(0, 0, 0, 0.05);
`;

const CurrencySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  width: 100%;
`;

const CurrencyList = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 12px;
  width: 100%;

  > *:nth-child(even) {
    text-align: right;
  }
`;

const SidebarHeadline = styled(Text)`
  width: 100%;
  text-align: center;
`;

const SidebarContent = styled(Flex)`
  width: 100%;
`;

const CurrencyValue = styled(Text)`
  small {
    font: inherit;
    color: ${({ theme }) => theme.secondary.blendToBackground(700)};
  }
`;

export const ModalInternationalCounterparty: FC<ModalInternationalCounterpartyProps> = ({
  isOpen,
  onClose,
  values,
  callback,
}) => {
  const tabsRef = useRef<Record<StepID, HTMLDivElement | null>>({
    bank: null,
    account: null,
    owner: null,
  });
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const { createRequest: fetchInstitution } = useInstitution();
  const { createRequest: createCounterparty } = useCreateCounterparty();
  const { createRequest: fetchTransfers } = useTransfersFetch();
  const { createRequest: deleteCounterparty } = useDeleteCounterparty();

  const { handleSubmitErrors } = useFormErrorHandler({ defaultDisplayType: 'popup' });

  const useFormMethods = useForm<FormData>({
    defaultValues: {
      temporaryData: {
        steps: [
          { id: 'bank', label: 'Beneficial Bank', state: 'active' },
          { id: 'account', label: 'Bank Account', state: 'disabled' },
          { id: 'owner', label: 'Account Owner', state: 'disabled' },
        ],
        institution: null,
        institutionSidebar: false,
        iban: null,
        flagImageData: null,
        countryCodeEntry: null,
        currency: null,
        oldCounterpartyId: null,
      },
    },
    mode: 'onChange',
    shouldFocusError: false,
  });
  const { handleSubmit, setValue, reset, watch, getValues, trigger, formState } = useFormMethods;

  useEffect(() => {
    const flagBackground = () => {
      if (!canvasRef.current) {
        return;
      }

      const canvas = canvasRef.current;
      const canvasBlur = document.createElement('canvas');
      canvasBlur.width = canvas.width;
      canvasBlur.height = canvas.height;
      const ctxBlur = canvasBlur.getContext('2d');

      if (ctxBlur) {
        ctxBlur.drawImage(canvas, 0, 0);

        ctxBlur.drawImage(canvasBlur, 0, 0);

        setValue('temporaryData.flagImageData', canvasBlur.toDataURL());
      }
    };

    const animationFrame = requestAnimationFrame(flagBackground);

    return () => cancelAnimationFrame(animationFrame);
  }, []);

  // Initialize form with values when modal opens
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (values) {
      const {
        routingNumber,
        routingNumberType,
        accountNumber,
        accountType,
        address,
        legalId,
        legalType,
        localBankCode,
        localAccountNumber,
        localBankCountryCode,
        localBankName,
        name,
        phone,
        email,
        description,
        id: oldCounterpartyId,
      }: Counterparty = values;

      reset();

      setTimeout(() => {
        setValue('routingNumber', routingNumber ?? '');
        setValue('routingNumberType', routingNumberType ?? 'aba');
        setValue('accountNumber', accountNumber ?? '');
        if (accountType) setValue('accountType', accountType as 'checking' | 'savings');
        setValue('address', address ?? {});
        setValue('legalId', legalId ?? '');
        setValue('legalType', legalType ?? '');
        setValue('localBankCode', localBankCode ?? '');
        setValue('localAccountNumber', localAccountNumber ?? '');
        setValue('localBankCountryCode', localBankCountryCode ?? '');
        setValue('localBankName', localBankName ?? '');
        setValue('name', name ?? '');
        setValue('phone', phone ?? '');
        setValue('email', email ?? '');
        setValue('description', description ?? '');

        setValue('temporaryData.oldCounterpartyId', oldCounterpartyId ?? null);

        if (routingNumber) {
          fetchInstitution({ routingNumber })
            .then((response) => {
              if (response) {
                setValue('temporaryData.institution', response);
                setValue('temporaryData.institutionSidebar', response !== null);

                const countryCodeEntry = countryCodeJSON.find((e) => e.countryCode === response?.countryCode) ?? null;

                setValue('temporaryData.countryCodeEntry', countryCodeEntry);
                setValue(
                  'temporaryData.currency',
                  Currencies.find(({ code }) => code === countryCodeEntry?.currencyCode) ?? null
                );

                setValue('routingNumberType', response?.routingNumberType ?? 'aba');

                setValue('localBankCountryCode', response?.countryCode === 'US' ? '' : (response?.countryCode ?? ''));
                setValue('address.countryCode', response?.countryCode ?? '');
              }
            })
            .catch((error) => {
              addDangerNotification({
                content: error.message,
                display: 'popup',
              });
            });
        }
      }, 0);
    } else {
      reset();
    }
  }, [isOpen, values]);

  const handleSubmitClick = useCallback(
    async (event: MouseEvent<HTMLButtonElement>, isSubmit: boolean) => {
      if (!isSubmit) {
        event.preventDefault();
      }

      const steps = getValues('temporaryData.steps');

      const currentIndex = steps.findIndex((step) => step.state === 'active');

      if (currentIndex === 0) {
        if (!watch('temporaryData.institution')) {
          addDangerNotification({
            content: 'Please add a valid Routing Number / BIC',
            display: 'popup',
          });

          return;
        }
      }

      if (currentIndex === -1) {
        return;
      }

      if (currentIndex === steps.length - 1) {
        return;
      }

      const updatedSteps = steps.map((step: StepType, index) => {
        if (index === currentIndex) {
          return { ...step, state: 'completed' };
        }
        if (index === currentIndex + 1) {
          return { ...step, state: 'active' };
        }
        return step;
      });

      setValue('temporaryData.steps', updatedSteps as StepType[]);
    },
    [formState, trigger]
  );

  const onSuccess = (data: FormData) => {
    const { temporaryData, ...newCounterparty } = data;

    const cleanedCounterparty = Object.fromEntries(
      Object.entries(newCounterparty).filter(([, value]) => value !== '')
    ) as typeof newCounterparty;

    createCounterparty(cleanedCounterparty)
      .then((response) => {
        if (!response) {
          return;
        }

        callback(response, temporaryData?.currency ?? undefined);

        const oldCounterpartyId = watch('temporaryData.oldCounterpartyId');

        if (oldCounterpartyId) {
          addSuccessNotification({
            content: 'Counterparty updated successfully',
          });

          fetchTransfers({ counterpartyId: oldCounterpartyId }).then((transferResponse) => {
            if ((transferResponse?.transfers ?? []).length === 0) {
              deleteCounterparty({ id: oldCounterpartyId });
            }
          });
        } else {
          addSuccessNotification({
            content: 'Counterparty created and selected successfully',
          });
        }

        onClose();
      })
      .catch((error) => {
        addDangerNotification({
          content: error.message,
          display: 'popup',
        });
      });
  };

  const handleCancelClick = useCallback(() => {
    const steps = getValues('temporaryData.steps');

    const currentIndex = steps.findIndex((step) => step.state === 'active');

    if (currentIndex === -1) {
      return;
    }

    if (currentIndex === 0) {
      onClose();

      return;
    }

    const updatedSteps = steps.map((step, index) => {
      if (index === currentIndex) {
        return { ...step, state: 'disabled' };
      }
      if (index === currentIndex - 1) {
        return { ...step, state: 'active' };
      }
      return step;
    });

    setValue('temporaryData.steps', updatedSteps as StepType[]);
  }, [onClose]);

  const showSidebar = watch('temporaryData.institutionSidebar');

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={ModalSize.FitContent}>
      <GlobalStyles />
      <ModalContainer $showSidebar={showSidebar}>
        <Sidebar>
          <SidebarInner $showSidebar={showSidebar}>
            <SidebarContent flexDirection="column" gap={24} alignItems="flex-start">
              <AddressIcon>
                <Flag
                  countryCode={watch('temporaryData.institution.countryCode')?.toLowerCase() ?? 'us'}
                  ref={canvasRef}
                  size={40}
                />
              </AddressIcon>

              <SidebarHeadline size={15} weight="medium" color="foreground">
                {titleCase(String(watch('temporaryData.institution')?.fullName))}
              </SidebarHeadline>

              <Flex gap={8} alignItems="center">
                <Icon.Globe />
                <Text size={14} weight="medium" color="foreground" lineHeight="20px">
                  {watch('temporaryData.countryCodeEntry.countryName')} (
                  {watch('temporaryData.institution')?.countryCode})
                </Text>
              </Flex>

              <Flex flexDirection="column" gap={8} alignItems="flex-start">
                <Flex gap={8} alignItems="center">
                  <Icon.Marker />
                  <Text size={14} weight="medium" color="foreground" lineHeight="20px">
                    Address
                  </Text>
                </Flex>
                <Flex flexDirection="column">
                  {watch('temporaryData.institution')?.streetAddress && (
                    <Text size={14} lineHeight="20px">
                      {titleCase(String(watch('temporaryData.institution')?.streetAddress))}
                    </Text>
                  )}
                  <Text size={14} lineHeight="20px">
                    {titleCase(String(watch('temporaryData.institution')?.city))}{' '}
                    {watch('temporaryData.institution')?.state} {watch('temporaryData.institution')?.zipCode}
                  </Text>
                </Flex>
              </Flex>

              {watch('temporaryData.currency') && (
                <CurrencySection>
                  <Flex gap={8} alignItems="center">
                    <Icon.Money />
                    <Text size={14} weight="medium" color="foreground" lineHeight="20px">
                      Currency (Default)
                    </Text>
                  </Flex>
                  <CurrencyList>
                    <Text size={14} weight="medium" color="light">
                      Name
                    </Text>
                    <CurrencyValue size={14} weight="semibold">
                      {watch('temporaryData.currency.nameSingle')}
                    </CurrencyValue>
                    <Text size={14} weight="medium" color="light">
                      Code & Symbol
                    </Text>
                    <CurrencyValue size={14} weight="semibold">
                      {watch('temporaryData.currency.code')} / {watch('temporaryData.currency.symbolNative')}
                    </CurrencyValue>
                    <Text size={14} weight="medium" color="light">
                      Decimal Digits
                    </Text>
                    <CurrencyValue size={14} weight="semibold">
                      {watch('temporaryData.currency.decimalDigits')}{' '}
                      <small>({formatNumber(0, watch('temporaryData.currency.code'))})</small>
                    </CurrencyValue>
                  </CurrencyList>
                </CurrencySection>
              )}
            </SidebarContent>
          </SidebarInner>
        </Sidebar>
        <Content>
          <Blur $backgroundBlur={watch('temporaryData.flagImageData') ?? undefined} />
          <Modal.Header
            title={watch('temporaryData.oldCounterpartyId') ? 'Edit Counterparty' : 'New Counterparty'}
            noBorder
          />
          <Steps>
            {watch('temporaryData.steps').map(({ id, label, state }, index) => (
              <Step $state={state} key={id}>
                <StepNumber $state={state}>{index + 1}</StepNumber>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Steps>

          <FormProvider {...useFormMethods}>
            <form
              style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
              onSubmit={(e) => {
                e.stopPropagation();
                handleSubmit(onSuccess, (errors) => handleSubmitErrors<FormData>(errors))(e);
              }}
            >
              <ModalContentWrapper>
                <Inner p={0} pb={24}>
                  <Modal.Content align="left">
                    <NotificationList display="popup" variant="light" spacing="top" />
                    <Tabs>
                      <Tab
                        $isActive={watch('temporaryData.steps').at(0)?.state === 'active'}
                        ref={(el) => {
                          tabsRef.current.bank = el;
                        }}
                      >
                        <ContentBank />
                      </Tab>
                      <Tab
                        $isActive={watch('temporaryData.steps').at(1)?.state === 'active'}
                        ref={(el) => {
                          tabsRef.current.account = el;
                        }}
                      >
                        <ContentAccount />
                      </Tab>
                      <Tab
                        $isActive={watch('temporaryData.steps').at(2)?.state === 'active'}
                        ref={(el) => {
                          tabsRef.current.owner = el;
                        }}
                      >
                        <ContentOwner />
                      </Tab>
                    </Tabs>
                  </Modal.Content>
                </Inner>
              </ModalContentWrapper>
              <Modal.Footer $withBackground>
                <Button
                  type="button"
                  variant="secondary"
                  icon={watch('temporaryData.steps').at(0)?.state !== 'active' ? <Icon.ChevronLeft /> : undefined}
                  onClick={handleCancelClick}
                >
                  {watch('temporaryData.steps').at(0)?.state === 'active' ? 'Cancel' : 'Back'}
                </Button>

                <Button
                  type={watch('temporaryData.steps').at(2)?.state === 'active' ? 'submit' : 'button'}
                  icon={
                    watch('temporaryData.steps').at(2)?.state === 'active' ? (
                      <Icon.CircleCheck />
                    ) : (
                      <Icon.ChevronRight />
                    )
                  }
                  iconRight={watch('temporaryData.steps').at(2)?.state !== 'active'}
                  onClick={(event) => handleSubmitClick(event, watch('temporaryData.steps').at(2)?.state === 'active')}
                >
                  {watch('temporaryData.steps').at(2)?.state === 'active' ? 'Submit' : 'Continue'}
                </Button>
              </Modal.Footer>
            </form>
          </FormProvider>
        </Content>
      </ModalContainer>
    </Modal>
  );
};
